import React, { useState, useEffect } from 'react';
import { User, Briefcase, Phone, MapPin, Copy, Check, Mail, Lock, RefreshCw, Eye, EyeOff, FileText, LogOut, Shield, Users } from 'lucide-react';
import { Alert, AlertDescription } from './components/ui/alert';
import useFormValidation from './useFormValidation';
import EmailGuides from './EmailGuides';
import SimpleExportMenu from './SimpleExportMenu';
import generateCredentialsPDF from './GenerateCredentialsPDF';
import AdminDashboard from './components/admin/AdminDashboard';
import SignaturePreview from './components/SignaturePreview';
import LoginForm from './components/auth/LoginForm';
import authService from './services/authService';
import './App.css';

// URL de base de l'API
// S'assurer que REACT_APP_API_URL dans .env est juste http://localhost:PORT_BACKEND (sans /api)
const API_HOST_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001'; // Ex: http://localhost:3001 ou '' (pour relatif)
const DOMAIN = 'plateau-urbain.com';

const App = () => {
  // État d'authentification
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  
  const [currentView, setCurrentView] = useState(window.location.hash || '#create-user');

  // Vérifier l'authentification au chargement
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (authService.isAuthenticated()) {
          const isValid = await authService.verifyToken();
          if (isValid) {
            setIsAuthenticated(true);
            setCurrentUser(authService.getUser());
          }
        }
      } catch (error) {
        console.error('Erreur lors de la vérification de l\'authentification:', error);
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      setCurrentView(window.location.hash || '#create-user');
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Fonctions d'authentification
  const handleLogin = async (username, password) => {
    setLoginLoading(true);
    try {
      const result = await authService.login(username, password);
      setIsAuthenticated(true);
      setCurrentUser(result.user);
      setCurrentView('#create-user'); // Rediriger vers la page principale
    } catch (error) {
      throw error; // Laisser le composant LoginForm gérer l'erreur
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      setIsAuthenticated(false);
      setCurrentUser(null);
      setCurrentView('#create-user');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    fonction: "",
    telephone: "",
    adresse1: "16 bld Saint-germain",
    adresse2: "CS 70514",
    codePostal: "75237",
    ville: "Paris cedex 05",
    email: "",
    password: "",
    groupEmails: []
  });

  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  useEffect(() => {
    if (isAuthenticated && (currentView === '#create-user' || currentView === '')) {
      const fetchGroups = async () => {
        setLoadingGroups(true);
        try {
          const response = await authService.authenticatedFetch(`${API_HOST_BASE}/api/groups`);
          const data = await response.json();
          if (response.ok && data.success) {
            setGroups(data.groups || []);
          }
        } catch (error) {
          console.error('Erreur lors du chargement des groupes:', error);
        } finally {
          setLoadingGroups(false);
        }
      };
      fetchGroups();
    }
  }, [isAuthenticated, currentView]);

  const [showPassword, setShowPassword] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);

  const generatePassword = () => {
    const uppercaseChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lowercaseChars = "abcdefghijklmnopqrstuvwxyz";
    const numberChars = "0123456789";
    const specialChars = "!@#$%^&*()_+-=[]{}|;:,.<>?";
    
    let password = "";
    password += uppercaseChars.charAt(Math.floor(Math.random() * uppercaseChars.length));
    password += lowercaseChars.charAt(Math.floor(Math.random() * lowercaseChars.length));
    password += numberChars.charAt(Math.floor(Math.random() * numberChars.length));
    password += specialChars.charAt(Math.floor(Math.random() * specialChars.length));
    
    const allChars = uppercaseChars + lowercaseChars + numberChars + specialChars;
    const remainingLength = 8;
    
    for (let i = 0; i < remainingLength; i++) {
      password += allChars.charAt(Math.floor(Math.random() * allChars.length));
    }
    
    password = password.split('');
    for (let i = password.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [password[i], password[j]] = [password[j], password[i]];
    }
    
    return password.join('');
  };

  const generateEmail = (prenom, nom) => {
    if (!prenom || !nom) return "";
    const normalizedPrenom = prenom.toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]/g, "");
    
    const normalizedNom = nom.toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]/g, "");
    
    return `${normalizedPrenom}.${normalizedNom}@${DOMAIN}`;
  };

  useEffect(() => {
    if (formData.prenom && formData.nom) {
      const generatedEmail = generateEmail(formData.prenom, formData.nom);
      setFormData(prev => ({ ...prev, email: generatedEmail }));
    }
  }, [formData.prenom, formData.nom]);

  useEffect(() => {
    setFormData(prev => ({ ...prev, password: generatePassword() }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    const { errors: newErrors } = validateForm({
      ...formData,
      [name]: value
    });
    setErrors(prev => ({ ...prev, [name]: newErrors[name] }));
  };    

  const handleRefreshPassword = () => {
    const newPassword = generatePassword();
    setFormData(prev => ({ ...prev, password: newPassword }));
    setPasswordCopied(false);
  };

  const copyPasswordToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(formData.password);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    } catch (err) {
      console.error("Erreur lors de la copie du mot de passe", err);
    }
  };

  const [errors, setErrors] = useState({});
  const { validateForm } = useFormValidation(formData);
  const [loading, setLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  const createEmailAccount = async () => {
    console.log("Création de compte email démarrée");
    const { isValid, errors: validationErrors } = validateForm(formData);
    
    if (!isValid) {
      console.log("Formulaire invalide :", validationErrors);
      setErrors(validationErrors);
      return;
    }
  
    setLoading(true);
    console.log("Envoi des données au serveur", formData);
    
    try {
      const createAccountResponse = await authService.authenticatedFetch(`${API_HOST_BASE}/api/users`, {
        method: 'POST',
        body: JSON.stringify({
          firstName: formData.prenom,
          lastName: formData.nom,
          email: formData.email,
          password: formData.password,
          signatureHtml: signatureTemplate,
          groupEmails: formData.groupEmails
        }),
      });
  
      let accountData;
      try {
        accountData = await createAccountResponse.json();
      } catch (jsonError) {
        console.error('Erreur de parsing JSON (create user):', jsonError);
        throw new Error('Format de réponse invalide du serveur (create user)');
      }
      
      if (!createAccountResponse.ok) {
        throw new Error(accountData.error || 'Erreur lors de la création du compte');
      }
      
      setEmailStatus({
        type: 'success',
        message: 'Compte email créé avec succès! Configuration de la signature en cours...'
      });
      
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      try {
        const setSignatureResponse = await authService.authenticatedFetch(`${API_HOST_BASE}/api/signatures`, {
          method: 'POST',
          body: JSON.stringify({
            email: formData.email,
            signature: signatureTemplate
          }),
        });
    
        let signatureData;
        try {
          signatureData = await setSignatureResponse.json();
        } catch (jsonError) {
          console.error('Erreur de parsing JSON (set signature):', jsonError);
          throw new Error('Format de réponse invalide du serveur (set signature)');
        }
        
        if (!setSignatureResponse.ok) {
          throw new Error(signatureData.error || 'Erreur lors de la configuration de la signature');
        }
        
        setEmailStatus({
          type: 'success',
          message: 'Compte email créé avec succès! La signature sera disponible dès la première connexion.',
          showPdfButton: true
        });
      } catch (signatureError) {
        console.error('Erreur de configuration de signature:', signatureError);
        setEmailStatus({
          type: 'warning',
          message: 'Compte email créé avec succès, mais la signature n\'a pas pu être configurée automatiquement. L\'utilisateur devra configurer sa signature manuellement après sa première connexion.',
          showPdfButton: true
        });
      }
      
    } catch (error) {
      console.error('Erreur complète dans createEmailAccount:', error);
      setEmailStatus({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = () => {
    const userData = {
      firstName: formData.prenom,
      lastName: formData.nom,
      email: formData.email,
      password: formData.password,
      fonction: formData.fonction,
      telephone: formData.telephone,
      adresse1: formData.adresse1,
      adresse2: formData.adresse2,
      codePostal: formData.codePostal,
      ville: formData.ville
    };
    
    generateCredentialsPDF(userData, signatureTemplate);
  };

  const signatureTemplate = `
    <!DOCTYPE html>
    <html xmlns="http://www.w3.org/1999/xhtml">
      <body>
        <table style="width: 100%; max-width: 525px; font-size: 11pt; font-family: Arial, sans-serif; border-collapse: collapse;" cellspacing="0" cellpadding="0">
          <tr>
            <td style="font-size: 10pt; font-family: Arial, sans-serif; border-right: 1px solid; border-right-color: #e9473c; width: 125px; padding-right: 10px; vertical-align: top;" rowspan="6" width="125" valign="top">
              <table cellspacing="0" cellpadding="0">
                <tr>
                  <td style="padding:5px">
                    <a href="https://plateau-urbain.com" target="_blank" rel="noopener">
                      <img src="https://plateforme.plateau-urbain.com/public/images/logo.png" style="width:100px; height:auto; border:0;" />
                    </a>
                  </td>
                </tr>
              </table>
            </td>
            <td style="padding-left:10px">
              <table cellspacing="0" cellpadding="0">
                <tr>
                  <td style="font-size: 10pt; color:#0079ac; font-family: Arial, sans-serif; width: 100%; padding-bottom: 5px; padding-left: 10px; vertical-align: top;" valign="top">
                    <strong><span style="font-size: 11pt; font-family: Arial, sans-serif; color:#e9473c;">${formData.nom} ${formData.prenom}</span></strong>
                    <strong style="font-family: Arial, sans-serif; font-size:11pt; color:#000000;">
                      <span> | </span>
                      ${formData.fonction}
                    </strong>
                  </td>
                </tr>
                <tr>
                  <td style="font-size: 10pt; color:#444444; font-family: Arial, sans-serif; padding: 10px; line-height: 17px;" valign="top">
                    <span style="font-size: 10pt; font-family: Arial, sans-serif; color:#000000;">📞 ${formData.telephone}</span>
                  </td>
                </tr>
                <tr>
                  <td style="font-size: 10pt; color:#444444; font-family: Arial, sans-serif; padding-bottom: 5px; padding-top: 5px; padding-left: 10px; vertical-align: top; line-height:17px;" valign="top">
                    <br><span style="font-size: 10pt; font-family: Arial, sans-serif; color: #000000;">${formData.adresse1}</span>
                    <br><span style="font-size: 10pt; font-family: Arial, sans-serif; color: #000000;">${formData.adresse2}</span>
                    <span style="font-size: 10pt; font-family: Arial, sans-serif; color: #000000;"><span> | </span>${formData.codePostal} ${formData.ville}</span>
                  </td>
                </tr>
                <tr>
                  <td style="padding-left: 10px;" class="flex space-x-2 pt-2">
                    <a href="https://facebook.com" target="_blank" rel="noopener"><img src="https://plateforme.plateau-urbain.com/public/images/facebook-48.png" width="20" /></a>
                    <a href="https://linkedin.com" target="_blank" rel="noopener"><img src="https://plateforme.plateau-urbain.com/public/images/linkedin-48.png" width="20" /></a>
                    <a href="https://instagram.com" target="_blank" rel="noopener"><img src="https://plateforme.plateau-urbain.com/public/images/instagram-48.png" width="20" /></a>
                  </td>
                </tr>
                <tr>
                  <td style="font-size: 8pt; font-family: Arial, sans-serif; color: #878787; text-align: justify; padding-top: 10px;">
                    L'intégrité de ce message n'étant pas assurée sur l'Internet, nous ne pouvons être tenus responsables de son contenu. Si vous n'êtes pas destinataire de ce message, merci de le détruire et d'en avertir l'expéditeur.
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </body>
    </html>
  `;

  const renderView = () => {
    switch (currentView) {
      case '#admin':
        return <AdminDashboard />;
      case '#create-user':
      default:
        return (
          <div className="min-h-screen bg-gray-100 flex flex-col items-center py-6 px-4">
            <div className="container mx-auto max-w-2xl bg-white p-8 rounded-lg shadow-lg">
              <div className="text-center mb-8">
                <img src="/logo-plateau-urbain.png" alt="Logo Plateau Urbain" className="mx-auto h-16 mb-4" />
                <h1 className="text-3xl font-bold text-gray-800">Générateur de compte E-mail</h1>
                <p className="text-gray-600 mt-2">Créez un nouveau compte utilisateur et configurez sa signature.</p>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); createEmailAccount(); }} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="prenom" className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input type="text" name="prenom" id="prenom" value={formData.prenom} onChange={handleChange} className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" placeholder="Ex: Anne" />
                    </div>
                    {errors.prenom && <p className="text-red-500 text-xs mt-1">{errors.prenom}</p>}
                  </div>
                  <div>
                    <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input type="text" name="nom" id="nom" value={formData.nom} onChange={handleChange} className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" placeholder="Ex: Dupont" />
                    </div>
                    {errors.nom && <p className="text-red-500 text-xs mt-1">{errors.nom}</p>}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="flex items-center text-sm font-medium text-gray-700">
                    <Briefcase className="w-4 h-4 mr-2 text-blue-500" />
                    Fonction
                  </label>
                  <input
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-200"
                    type="text"
                    name="fonction"
                    placeholder="Votre fonction"
                    value={formData.fonction}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <label className="flex items-center text-sm font-medium text-gray-700">
                    <Phone className="w-4 h-4 mr-2 text-blue-500" />
                    Téléphone
                  </label>
                  <input
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-200"
                    type="text"
                    name="telephone"
                    placeholder="Votre numéro de téléphone"
                    value={formData.telephone}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <label className="flex items-center text-sm font-medium text-gray-700">
                    <MapPin className="w-4 h-4 mr-2 text-blue-500" />
                    Adresse
                  </label>
                  <input
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-200"
                    type="text"
                    name="adresse1"
                    placeholder="Adresse ligne 1"
                    value={formData.adresse1}
                    onChange={handleChange}
                  />
                  <input
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-200"
                    type="text"
                    name="adresse2"
                    placeholder="Adresse ligne 2"
                    value={formData.adresse2}
                    onChange={handleChange}
                  />
                  <div className="flex space-x-4">
                    <input
                      className="w-1/3 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-200"
                      type="text"
                      name="codePostal"
                      placeholder="Code postal"
                      value={formData.codePostal}
                      onChange={handleChange}
                    />
                    <input
                      className="w-2/3 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-200"
                      type="text"
                      name="ville"
                      placeholder="Ville"
                      value={formData.ville}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                    <Users className="w-4 h-4 mr-2 text-blue-500" />
                    Groupes Google (Optionnel)
                  </label>
                  <div className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white max-h-48 overflow-y-auto">
                    {loadingGroups && <p className="text-xs text-gray-500">Chargement des groupes en cours...</p>}
                    {!loadingGroups && groups.length === 0 && <p className="text-sm text-gray-500">Aucun groupe trouvé.</p>}
                    {!loadingGroups && groups.map(group => (
                      <div key={group.id} className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          id={`group-${group.id}`}
                          value={group.email}
                          checked={formData.groupEmails.includes(group.email)}
                          onChange={(e) => {
                            const newGroupEmails = e.target.checked
                              ? [...formData.groupEmails, group.email]
                              : formData.groupEmails.filter(email => email !== group.email);
                            setFormData(prev => ({ ...prev, groupEmails: newGroupEmails }));
                          }}
                          className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                        />
                        <label htmlFor={`group-${group.id}`} className="ml-2 text-sm font-medium text-gray-700 cursor-pointer">
                          {group.name ? `${group.name} (${group.email})` : group.email}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email généré</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input type="email" name="email" id="email" value={formData.email} readOnly className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md shadow-sm bg-gray-50" />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Mot de passe généré</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input type={showPassword ? "text" : "password"} name="password" id="password" value={formData.password} readOnly className="pl-10 pr-20 py-2 w-full border border-gray-300 rounded-md shadow-sm bg-gray-50" />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="p-1 text-gray-500 hover:text-gray-700">
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                      <button type="button" onClick={handleRefreshPassword} className="p-1 text-gray-500 hover:text-gray-700">
                        <RefreshCw size={18} />
                      </button>
                      <button type="button" onClick={copyPasswordToClipboard} className="p-1 text-gray-500 hover:text-green-600">
                        {passwordCopied ? <Check size={18} className="text-green-600" /> : <Copy size={18} />}
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Ajout de la prévisualisation de signature */}
                <SignaturePreview signatureTemplate={signatureTemplate} formData={formData} />
                
                {emailStatus && (
                  <Alert variant={emailStatus.type === 'error' ? 'destructive' : (emailStatus.type === 'warning' ? 'warning' : 'default')} className={`mt-4 ${emailStatus.type === 'success' ? 'bg-green-50 border-green-300' : emailStatus.type === 'error' ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-300'}`}>
                    <AlertDescription className={`text-sm ${emailStatus.type === 'success' ? 'text-green-700' : emailStatus.type === 'error' ? 'text-red-700' : 'text-yellow-700'}`}>
                      {emailStatus.message}
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex flex-wrap items-center justify-between gap-4 mt-8">
                  <SimpleExportMenu formData={formData} signatureTemplate={signatureTemplate} />
                  
                  {/* Bouton PDF de prévisualisation - toujours visible */}
                  <button 
                    type="button" 
                    onClick={handleGeneratePDF} 
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    title="Générer un PDF avec les informations actuelles"
                  >
                    <FileText size={18} className="mr-2" />
                    Aperçu PDF
                  </button>

                  {/* Bouton PDF des identifiants finaux - seulement après création réussie */}
                  {emailStatus && emailStatus.type === 'success' && emailStatus.showPdfButton && (
                       <button type="button" onClick={handleGeneratePDF} className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                          <FileText size={18} className="mr-2" />
                          Identifiants finaux (PDF)
                      </button>
                  )}
                   <button 
                      type="submit" 
                      disabled={loading} 
                      className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50">
                      {loading ? (
                          <>
                              <RefreshCw className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                              Création en cours...
                          </>
                      ) : 'Créer le compte et la signature'}
                  </button>
                </div>
              </form>

              <EmailGuides />
            </div>
            <footer className="text-center text-sm text-gray-500 mt-8">
              Générateur de comptes v1.3 - Plateau Urbain - 
              <a href="https://github.com/plateau-urbain/email-signature-generator" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700">Code source sur GitHub</a>
            </footer>
          </div>
        );
    }
  };

  // Affichage de chargement pendant la vérification d'authentification
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }

  // Affichage du formulaire de connexion si non authentifié
  if (!isAuthenticated) {
    return <LoginForm onLogin={handleLogin} isLoading={loginLoading} />;
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-indigo-600 text-white shadow-lg">
        <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex-shrink-0">
              <a href="#create-user" className="text-xl font-bold hover:text-indigo-200">
                Email & Signature Manager
              </a>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                <a 
                  href="#create-user" 
                  className={`px-3 py-2 rounded-md text-sm font-medium ${currentView === '#create-user' || currentView === '' ? 'bg-indigo-700 text-white' : 'text-indigo-100 hover:bg-indigo-500 hover:bg-opacity-75'}`}
                >
                  Créer un compte
                </a>
                {/* Afficher l'admin seulement si l'utilisateur a les permissions */}
                {currentUser && authService.hasPermission('manage_signatures') && (
                  <a 
                    href="#admin" 
                    className={`px-3 py-2 rounded-md text-sm font-medium ${currentView === '#admin' ? 'bg-indigo-700 text-white' : 'text-indigo-100 hover:bg-indigo-500 hover:bg-opacity-75'}`}
                  >
                    <Shield className="inline-block w-4 h-4 mr-1" />
                    Admin Dashboard
                  </a>
                )}
              </div>
            </div>
            
            {/* Informations utilisateur et déconnexion */}
            <div className="flex items-center space-x-4">
              <div className="text-sm">
                <span className="text-indigo-200">Connecté en tant que</span>
                <span className="font-medium ml-1">{currentUser?.username}</span>
                <span className="text-xs text-indigo-300 ml-2">({currentUser?.role})</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-indigo-100 hover:bg-indigo-500 hover:bg-opacity-75 transition-colors"
              >
                <LogOut className="w-4 h-4 mr-1" />
                Déconnexion
              </button>
            </div>
          </div>
        </nav>
      </header>

      <main>
        {renderView()}
      </main>

      <footer className="bg-slate-800 text-slate-300 text-center p-4 mt-8">
        <p>&copy; {new Date().getFullYear()} Gestionnaire de Signatures. Tous droits réservés.</p>
      </footer>
    </div>
  );
  };

export default App;